import { useEffect, useState } from 'react';
import { useStore } from 'react-redux';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { fetchDiscrepancySettings, type DiscrepancySettings } from './settings';

export interface UseDiscrepancySettingsResult {
  /** Whether the settings are currently being fetched */
  loading: boolean;
  /** The fetched discrepancy settings (disabled if still loading) */
  settings: DiscrepancySettings;
  /** Error message if the fetch failed, or null if no error */
  error: string | null;
}

/**
 * React hook to fetch and manage discrepancy settings for the current organization.
 * Automatically fetches settings when the component mounts or when the org changes.
 */
export function useDiscrepancySettings(): UseDiscrepancySettingsResult {
  const orgName = useCurrentOrgName();
  const [settings, setSettings] = useState<DiscrepancySettings>({
    enabled: false,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const store = useStore();

  useEffect(() => {
    setLoading(true);
    setError(null);

    if (!orgName) {
      setLoading(false);
      setError('No organization name available');
      setSettings({ enabled: false });
      return;
    }

    fetchDiscrepancySettings(orgName, store)
      .then(fetchedSettings => {
        setSettings(fetchedSettings);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        setSettings({ enabled: false });
        setLoading(false);
      });
  }, [orgName, store]);

  return {
    settings,
    loading,
    error,
  };
}
